import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, Link, Navigate } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { toast } from '../../stores/toast.store';
import { confirm as confirmDialog } from '../../stores/confirm.store';
import { Icon } from '../../components/ui/Icon';
import { Breadcrumb } from '../../components/ui/Breadcrumb';
import { Chip } from '../../components/ui/Chip';
import { ActionsMenu, type ActionItem } from '../../components/ui/ActionsMenu';
import { AnimatedNumber } from '../../components/ui/AnimatedNumber';
import { EmptyState } from '../../components/ui/EmptyState';
import { useAuthStore } from '../../stores/auth.store';
import { DetailSkeleton } from '../../components/ui/Skeleton';
import { DesktopOnly } from '../../components/ui/DesktopOnly';
import { fmtEur, fmtDate, fmtDateShort } from '../../lib/utils';
import {
  SERVICE_STATUS_LABEL,
  MANAGED_SERVICE_LABEL,
  ServiceStatus,
  ServiceEventKind,
  type ManagedServiceType,
} from '@masamune/shared';
import type { ServiceBilling } from './subscriptions.types';
import './subscriptions.css';

interface ServiceEvent {
  id: string;
  kind: keyof typeof ServiceEventKind;
  message: string;
  metadata: Record<string, unknown>;
  createdAt: string;
}

interface ServiceDetail {
  id: string;
  type: ManagedServiceType;
  provider: 'STRIPE' | 'HIGHLEVEL' | 'MANUAL';
  status: keyof typeof ServiceStatus;
  billingCycle: 'MONTHLY' | 'QUARTERLY' | 'YEARLY';
  planCode: string;
  planLabel: string;
  amount: number;
  /** Il prezzo di cartellino: se e' piu' alto di `amount` c'e' uno sconto perpetuo. */
  listAmount: number;
  costAmount: number;
  currency: string;
  /** Porta `discountReason` e i dati del cliente su Stripe quando l'anagrafica manca. */
  metadata: Record<string, unknown>;
  externalId: string | null;
  externalUrl: string | null;
  startedAt: string | null;
  currentPeriodEnd: string | null;
  trialEndsAt: string | null;
  cancelledAt: string | null;
  lastSyncedAt: string | null;
  notes: string | null;
  // Nullable: vedi ServicesPage — gli abbonamenti Stripe non collegati con
  // certezza restano senza cliente finche' qualcuno non li aggancia a mano.
  client: { id: string; legalName: string; shortName: string } | null;
  events: ServiceEvent[];
  createdAt: string;
  updatedAt: string;
}

const EVENT_ICON: Record<keyof typeof ServiceEventKind, { icon: 'check' | 'alert' | 'invoice' | 'link' | 'plus' | 'arrowRight' | 'arrowDown' | 'mail'; tone: 'ok' | 'warn' | 'alert' | 'info' | 'ghost' }> = {
  CREATED:        { icon: 'plus',       tone: 'info' },
  ACTIVATED:      { icon: 'check',      tone: 'ok' },
  PAUSED:         { icon: 'arrowDown',  tone: 'warn' },
  RESUMED:        { icon: 'arrowRight', tone: 'ok' },
  CANCELLED:      { icon: 'alert',      tone: 'alert' },
  PAYMENT_OK:     { icon: 'check',      tone: 'ok' },
  PAYMENT_FAILED: { icon: 'alert',      tone: 'alert' },
  PLAN_CHANGED:   { icon: 'arrowRight', tone: 'info' },
  RENEWED:        { icon: 'check',      tone: 'ok' },
  SYNCED:         { icon: 'link',       tone: 'info' },
  NOTE:           { icon: 'mail',       tone: 'ghost' },
};

function providerLabel(p: ServiceDetail['provider']): string {
  return p === 'STRIPE' ? 'Stripe' : p === 'HIGHLEVEL' ? 'HighLevel' : 'Manuale';
}

function statusChipTone(s: ServiceDetail['status']): 'ok' | 'info' | 'warn' | 'alert' | 'ghost' {
  switch (s) {
    case 'ACTIVE':    return 'ok';
    case 'TRIALING':  return 'info';
    case 'PAST_DUE':  return 'warn';
    case 'PAUSED':    return 'ghost';
    case 'CANCELLED': return 'alert';
    case 'EXPIRED':   return 'alert';
  }
}

function cycleLabel(c: ServiceDetail['billingCycle']): string {
  return c === 'MONTHLY' ? 'mensile' : c === 'QUARTERLY' ? 'trimestrale' : 'annuale';
}

export function ServiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const [noteText, setNoteText] = useState('');
  const user = useAuthStore((st) => st.user);
  const showMargin = !!user?.role.canSeeMargin;

  if (!id) return <Navigate to="/services" replace />;

  const { data, isLoading, isError } = useQuery({
    queryKey: ['service', id],
    queryFn: () => api.get<ServiceDetail>(`/services/${id}`),
    retry: false,
  });

  // Le fatture che corrispondono a questo canone. Query separata di proposito:
  // e' un accoppiamento per importo, non un dato certo, e se fallisce (o se
  // l'utente non vede Amministrazione) la scheda deve restare in piedi lo stesso.
  const { data: billing } = useQuery({
    queryKey: ['service', id, 'billing'],
    queryFn: () => api.get<ServiceBilling>(`/services/${id}/billing`),
    retry: false,
  });

  const sync = useMutation({
    mutationFn: () => api.post(`/services/${id}/sync`),
    onSuccess: () => {
      toast.ok('Sincronizzazione completata');
      qc.invalidateQueries({ queryKey: ['service', id] });
      qc.invalidateQueries({ queryKey: ['services'] });
    },
    onError: (err) => toast.alert('Errore sincronizzazione', err instanceof ApiError ? err.message : 'Riprova'),
  });

  const cancel = useMutation({
    mutationFn: () => api.post(`/services/${id}/cancel`),
    onSuccess: () => {
      toast.ok('Abbonamento cancellato');
      qc.invalidateQueries({ queryKey: ['service', id] });
      qc.invalidateQueries({ queryKey: ['services'] });
    },
    onError: (err) => toast.alert('Errore cancellazione', err instanceof ApiError ? err.message : 'Riprova'),
  });

  const updateStatus = useMutation({
    mutationFn: (status: keyof typeof ServiceStatus) => api.patch(`/services/${id}`, { status }),
    onSuccess: () => {
      toast.ok('Stato aggiornato');
      qc.invalidateQueries({ queryKey: ['service', id] });
      qc.invalidateQueries({ queryKey: ['services'] });
    },
    onError: (err) => toast.alert('Errore aggiornamento', err instanceof ApiError ? err.message : 'Riprova'),
  });

  const pause = useMutation({
    mutationFn: () => api.post(`/services/${id}/pause`),
    onSuccess: () => {
      toast.ok('Abbonamento sospeso', data?.provider === 'STRIPE' ? 'pause_collection attivata su Stripe' : undefined);
      qc.invalidateQueries({ queryKey: ['service', id] });
      qc.invalidateQueries({ queryKey: ['services'] });
    },
    onError: (err) => toast.alert('Errore pausa', err instanceof ApiError ? err.message : 'Riprova'),
  });

  const resume = useMutation({
    mutationFn: () => api.post(`/services/${id}/resume`),
    onSuccess: () => {
      toast.ok('Abbonamento riattivato', data?.provider === 'STRIPE' ? 'pause_collection rimossa su Stripe' : undefined);
      qc.invalidateQueries({ queryKey: ['service', id] });
      qc.invalidateQueries({ queryKey: ['services'] });
    },
    onError: (err) => toast.alert('Errore resume', err instanceof ApiError ? err.message : 'Riprova'),
  });

  const addNote = useMutation({
    mutationFn: (message: string) => api.post(`/services/${id}/events`, { kind: 'NOTE', message }),
    onSuccess: () => {
      toast.ok('Nota aggiunta');
      setNoteText('');
      qc.invalidateQueries({ queryKey: ['service', id] });
    },
    onError: (err) => toast.alert('Errore', err instanceof ApiError ? err.message : 'Riprova'),
  });

  if (isError) {
    return (
      <div className="page">
        <Breadcrumb items={[{ label: 'Abbonamenti', to: '/services' }, { label: 'Abbonamento' }]} />
        {/* Not-found standard delle detail page (stesso pattern di clienti/progetti/fatture) */}
        <div className="max-w-[480px] mx-auto py-20 text-center">
          <h1 className="t-h1">Abbonamento non trovato</h1>
          <div className="mute mt-2">L'abbonamento richiesto potrebbe essere stato cancellato.</div>
        </div>
      </div>
    );
  }

  // Skeleton condiviso delle detail page: stessa silhouette, zero layout-shift
  if (isLoading || !data) return <DetailSkeleton header="chips" />;

  const s = data;
  const margin = s.amount - s.costAmount;
  const marginPct = s.amount > 0 ? (margin / s.amount) * 100 : 0;

  const handleCancel = async () => {
    const ok = await confirmDialog({
      title: 'Cancellare l\'abbonamento?',
      body: s.provider === 'STRIPE'
        ? 'La subscription verrà cancellata su Stripe: l\'operazione è IRREVERSIBILE.'
        : "L'abbonamento verrà segnato come cancellato (solo in locale).",
      danger: true,
      confirmLabel: 'Cancella',
    });
    if (ok) cancel.mutate();
  };

  // Azioni di stato abbonamento (kebab header): le stesse mutation di prima,
  // ora in un unico ActionsMenu. Cancella è danger + passa da confirmDialog.
  const stateActions: ActionItem[] = [
    {
      key: 'pause',
      label: s.provider === 'STRIPE' ? 'Sospendi (su Stripe)' : 'Sospendi',
      icon: 'arrowDown',
      onClick: () => pause.mutate(),
      hidden: s.status !== 'ACTIVE',
      disabled: pause.isPending,
    },
    {
      key: 'resume',
      label: s.provider === 'STRIPE' ? 'Riattiva (su Stripe)' : 'Riattiva',
      icon: 'arrowRight',
      onClick: () => resume.mutate(),
      hidden: s.status !== 'PAUSED',
      disabled: resume.isPending,
    },
    {
      key: 'force-active',
      label: 'Forza attivazione (locale)',
      icon: 'check',
      onClick: () => updateStatus.mutate('ACTIVE'),
      hidden: s.status !== 'TRIALING',
      disabled: updateStatus.isPending,
    },
    {
      key: 'cancel',
      label: s.provider === 'STRIPE' ? 'Cancella (su Stripe)' : 'Cancella',
      icon: 'trash',
      onClick: () => { void handleCancel(); },
      danger: true,
      hidden: s.status === 'CANCELLED',
      disabled: cancel.isPending,
    },
  ];

  return (
    // m-detail: sotto i 768px fa collassare a colonna sola la griglia
    // cronologia/anagrafica (os-planning/mobile.md §7). sb-detail e' mia e
    // serve solo a far andare a capo le azioni, che sul telefono schiacciavano
    // il titolo fino a "P.". Sul computer nessuna delle due fa niente.
    <div className="page m-detail sb-detail">
      <Breadcrumb items={[{ label: 'Abbonamenti', to: '/services' }, { label: s.planLabel }]} />
      {/* page-ed-hd + ed-title, come le altre dieci schede di dettaglio del
          gestionale. Era l'unica rimasta con .page-header e .t-h1: il titolo
          usciva a 36px invece di 43, e sotto c'erano 32px di stacco invece di
          22+10. Passando da una scheda all'altra si vedeva il salto. */}
      <div className="page-ed-hd flex items-end justify-between gap-4">
        <div className="min-w-0 max-w-[760px]">
          {/* L1 protagonista: il nome del piano (unico bold), tronca se lungo */}
          <h1 className="ed-title truncate">{s.planLabel}</h1>
          {/* Una riga sola di contesto: tipo · provider · ciclo · cliente.
              Erano due righe, e la scheda del progetto — che e' la sua gemella —
              ne usa una sola con la stessa grammatica (divisione · modello ·
              Cliente · PM). Con quattro righe a sinistra i bottoni a destra,
              allineati in basso, finivano a galleggiare cento pixel sotto il
              titolo. flex-wrap: .detail-meta e' un flex senza ritorno a capo, e
              su 390px spezzava le parole invece dei dati ("Hosting /
              Masamune"). */}
          <div className="detail-meta mt-2 flex-wrap">
            <span>{MANAGED_SERVICE_LABEL[s.type]}</span>
            <span className="mute-2">·</span>
            <span>{providerLabel(s.provider)}</span>
            <span className="mute-2">·</span>
            <span>ciclo {cycleLabel(s.billingCycle)}</span>
            <span className="mute-2">·</span>
            {/* Il rimando al cliente punta alla scheda in Contatti, che e' dove
                Lorenzo lo cerca. Senza anagrafica si mostra comunque il nome che
                sta su Stripe: e' l'unico modo per capire di chi si tratta. */}
            <span><span className="mute-2 mr-1.5">Cliente</span>{s.client
              ? <Link to={`/contatti/client/${s.client.id}`} className="v sb-link">{s.client.legalName}</Link>
              : <span className="v mute">
                  {(s.metadata?.stripeCustomerName as string) ?? 'Da collegare'}
                  <span className="mute-2 ml-1.5 text-[12px]">non in rubrica</span>
                </span>}</span>
          </div>
          {/* Stato: riga dedicata SOTTO, mai a competere col nome; un solo chip colorato */}
          <div className="flex gap-1.5 mt-2.5 items-center">
            <Chip tone={statusChipTone(s.status)} dot>{SERVICE_STATUS_LABEL[s.status]}</Chip>
          </div>
        </div>
        {/* pb-1 come nelle altre schede: i bottoni appoggiano sulla linea di
            base del titolo. Le due azioni frequenti sono d-only perche' sul
            telefono scendono in fondo (os-planning/mobile.md §4); il menu
            resta, perche' Sospendi/Riattiva/Cancella non spariscono dal
            telefono, semplicemente non stanno sotto il pollice. */}
        <div className="flex gap-2 pb-1 items-center">
          <span className="flex gap-2 d-only">
            {s.externalUrl && (
              <a className="btn" href={s.externalUrl} target="_blank" rel="noreferrer">
                <Icon name="link" /> Apri su {providerLabel(s.provider)}
              </a>
            )}
            <button className="btn btn-primary" onClick={() => sync.mutate()} disabled={sync.isPending}>
              <Icon name="refresh" /> {sync.isPending ? 'Sync…' : 'Sincronizza'}
            </button>
          </span>
          <DesktopOnly
            cosa="La sospensione o cancellazione dell'abbonamento"
            perche="sono modifiche amministrative da verificare al computer; dal telefono puoi sincronizzare e aprire il provider."
          >
            <ActionsMenu variant="ghost" items={stateActions} />
          </DesktopOnly>
        </div>
      </div>

      {/* Strip KPI: numeri NUDI sul foglio (stat-band) — costo e margine sono
          dati sensibili: gating canSeeMargin come nella lista servizi */}
      <div className="stat-band">
        <div>
          <div className="st-label">Ricavo per ciclo</div>
          <div className="st-value"><span className="u">€</span><AnimatedNumber value={s.amount} format={fmtEur} /></div>
          <div className="st-delta">{cycleLabel(s.billingCycle)}</div>
        </div>
        {showMargin ? (
          <div>
            <div className="st-label">Costo provider</div>
            <div className="st-value"><span className="u">€</span><AnimatedNumber value={s.costAmount} format={fmtEur} /></div>
            <div className="st-delta">{(((s.costAmount) / Math.max(s.amount, 1)) * 100).toFixed(0)}% del ricavo</div>
          </div>
        ) : (
          <div>
            <div className="st-label">Costo provider</div>
            <div className="st-value mute-2">•••</div>
            <div className="st-delta">Riservato</div>
          </div>
        )}
        {/* Margine = metrica ordinaria: niente semaforo */}
        {showMargin ? (
          <div>
            <div className="st-label">Margine</div>
            <div className="st-value"><span className="u">€</span><AnimatedNumber value={margin} format={fmtEur} /></div>
            <div className="st-delta">{marginPct.toFixed(1)}%</div>
          </div>
        ) : (
          <div>
            <div className="st-label">Margine</div>
            <div className="st-value mute-2">•••</div>
            <div className="st-delta">Riservato</div>
          </div>
        )}
        <div>
          <div className="st-label">Prossimo ciclo</div>
          <div className="st-value">{s.currentPeriodEnd ? fmtDate(s.currentPeriodEnd) : '—'}</div>
          {/* "Trial fino al" solo se il trial e' DAVVERO in corso: su Stripe
              trialEndsAt resta valorizzato anche a trial finito da due anni, e
              scriverlo al presente faceva sembrare in prova un cliente pagante. */}
          <div className="st-delta">
            {s.trialEndsAt && new Date(s.trialEndsAt) > new Date()
              ? `Trial fino al ${fmtDate(s.trialEndsAt)}`
              : s.cancelledAt ? `Cancellato il ${fmtDate(s.cancelledAt)}` : 'in corso'}
          </div>
        </div>
      </div>

      {/* Se si incassa meno del listino si dice subito, e si dice PERCHE'.
          Senza questa riga un canone a zero sembra un dato mancante invece che
          un servizio regalato, ed e' la differenza fra un bug e una decisione. */}
      {s.listAmount > s.amount && (
        <div className="adm-note">
          <Icon name="alert" size={12} className={s.amount === 0 ? 'text-alert' : 'mute'} />
          <span>
            {s.amount === 0 ? (
              <>Questo abbonamento <strong>non incassa niente</strong>: a listino sono € {fmtEur(s.listAmount)} {cycleLabel(s.billingCycle)}.</>
            ) : (
              <>Sconto permanente: si incassano <strong>€ {fmtEur(s.amount)}</strong> invece di € {fmtEur(s.listAmount)} {cycleLabel(s.billingCycle)}.</>
            )}
            {typeof s.metadata?.discountReason === 'string' && (
              <> {s.metadata.discountReason as string}.</>
            )}
          </span>
        </div>
      )}

      {/* 2fr 1fr, la proporzione delle altre nove schede a due colonne. Era
          1.6fr 1fr: la colonna dei dati anagrafici era piu' larga qui che
          altrove, e i due titoli di sezione non partivano dalla stessa x. */}
      <div className="grid gap-6" style={{ gridTemplateColumns: '2fr 1fr' }}>
        {/* LEFT: timeline eventi NUDA sul foglio (kicker + lista, niente gabbia) */}
        <div className="min-w-0">
          <div className="ed-section">
            <div className="data-hd">
              <span className="t-micro">Cronologia eventi</span>
              <span className="mute text-[12px] mono">{s.events.length} eventi</span>
            </div>
            {s.events.length === 0 && (
              <EmptyState icon="list" title="Nessun evento registrato" description="Sync, pagamenti e note compariranno qui." />
            )}
            {s.events.map((e) => {
              const meta = EVENT_ICON[e.kind];
              // Stati semantici (ok/warn/alert) tengono il loro colore; info e note
              // non sono uno stato reale -> dot viola-soft (niente blu/grigio decorativo).
              const semanticClass =
                meta.tone === 'ok' ? 'activity-budget'
                : meta.tone === 'warn' ? 'activity-comm'
                : meta.tone === 'alert' ? 'activity-alert'
                : '';
              const softStyle = semanticClass
                ? undefined
                : {
                    background: 'color-mix(in oklab, var(--accent) 11%, transparent)',
                    color: 'color-mix(in oklab, var(--accent) 72%, var(--text))',
                  };
              return (
                <div key={e.id} className="activity-row">
                  {/* Il codice evento (enum interno) resta in disclosure sul dot, non come colonna */}
                  <div className={`activity-dot ${semanticClass}`} style={softStyle} title={e.kind}>
                    <Icon name={meta.icon} size={12} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div>{e.message}</div>
                    <div className="mute mono text-[12px] mt-0.5">{fmtDateShort(e.createdAt)}</div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Il composer resta card: gli input hanno bisogno del foglio */}
          <div className="card mt-4">
            <div className="card-bd">
              <div className="flex gap-2 w-full">
                <input
                  className="inp"
                  placeholder="Aggiungi una nota a questo abbonamento…"
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && noteText.trim()) addNote.mutate(noteText.trim()); }}
                />
                {/* Una sola primaria per vista (Sincronizza nell'header): qui basta il bottone base */}
                <button
                  className="btn"
                  disabled={!noteText.trim() || addNote.isPending}
                  onClick={() => addNote.mutate(noteText.trim())}
                >
                  <Icon name="plus" size={12} /> Nota
                </button>
              </div>
            </div>
          </div>

          {/* ── Fatture collegate ──────────────────────────────────────────
              Il collegamento NON e' certo: le fatture le emette un'automazione
              esterna che non porta con se' l'id della subscription, quindi qui
              si accoppia per cliente + importo identico. Lo si scrive, invece
              di far passare un'ipotesi per un fatto. */}
          {billing && (
            <div className="ed-section mt-6">
              <div className="data-hd">
                <span className="t-micro">Fatture di pari importo</span>
                <span className="mute text-[12px] mono">{billing.matched.length}</span>
              </div>
              {billing.reason === 'no_client' && (
                <div className="mute text-[13px] py-3">
                  Senza un cliente collegato non si possono cercare le fatture: prima va agganciata l'anagrafica.
                </div>
              )}
              {billing.reason === 'zero_amount' && (
                <div className="mute text-[13px] py-3">
                  Il canone e&apos; zero, quindi non c&apos;e&apos; nessuna fattura da cercare.
                </div>
              )}
              {!billing.reason && billing.matched.length === 0 && (
                <div className="mute text-[13px] py-3">
                  Nessuna fattura di questo importo per questo cliente. Puo&apos; voler dire che il canone non e&apos;
                  ancora stato fatturato, oppure che e&apos; stato fatturato insieme ad altro.
                </div>
              )}
              {billing.matched.map((f) => (
                <div key={f.id} className="sb-row">
                  <span className="d">{fmtDateShort(f.issueDate)}</span>
                  <span className="n">
                    <Link to={`/invoices/${f.id}`} className="t sb-link">{f.number}</Link>
                    <span className="s">{f.status.toLowerCase()}</span>
                  </span>
                  <span className="a mono">€ {fmtEur(f.total)}</span>
                </div>
              ))}
              {billing.matched.length > 0 && (
                <div className="mute text-[12px] mt-3" style={{ lineHeight: 1.5 }}>
                  Accoppiate per cliente e importo identico, non da un collegamento diretto: una fattura dello stesso
                  importo emessa per un altro motivo finirebbe comunque in questo elenco.
                  {billing.otherCount > 0 && <> Altre {billing.otherCount} fatture dello stesso cliente hanno importi diversi.</>}
                </div>
              )}
            </div>
          )}
        </div>

        {/* RIGHT: kicker-blocks senza bordi, impilati con hairline (niente card) */}
        <div className="min-w-0">
          <div className="data-hd"><span className="t-micro">Anagrafica</span></div>
          {/* Cliente e provider vivono già nell'header: qui solo i dati non duplicati */}
          <div className="ed-kv">
            <div className="ed-kv-row"><div className="k">Codice piano</div><div className="v mono">{s.planCode}</div></div>
            <div className="ed-kv-row"><div className="k">External ID</div><div className="v mono text-[12px] mute truncate">{s.externalId ?? '—'}</div></div>
            <div className="ed-kv-row"><div className="k">Avvio</div><div className="v mono">{fmtDate(s.startedAt)}</div></div>
            <div className="ed-kv-row"><div className="k">Ultimo sync</div><div className="v mono">{s.lastSyncedAt ? fmtDateShort(s.lastSyncedAt) : '—'}</div></div>
          </div>

          {s.notes && (
            <>
              <div className="ed-divider" />
              <div className="data-hd"><span className="t-micro">Note interne</span></div>
              <div className="whitespace-pre-wrap">{s.notes}</div>
            </>
          )}

          <div className="ed-divider" />

          <div className="data-hd"><span className="t-micro">Gestione abbonamento</span></div>
          <div className="flex flex-col gap-2">
            <div className="mute text-[13px]">
              {s.provider === 'STRIPE'
                ? <>Le azioni di stato propagano <strong>direttamente su Stripe</strong> via API + sono confermate dal webhook in tempo reale.</>
                : <>Abbonamento {providerLabel(s.provider).toLowerCase()}: i cambi di stato sono solo locali (non collegato a un provider esterno).</>}
            </div>
            {/* items-start: con due righe di testo l'icona centrata finiva in
                mezzo al paragrafo e il filo sinistro del blocco ballava. */}
            <div className="mute text-[13px] flex items-start gap-1.5">
              <Icon name="more" size={12} className="mt-1 flex-none" />
              <span>Sospendi, Riattiva e Cancella sono nel menu azioni in alto a destra.</span>
            </div>
          </div>
        </div>
      </div>

      {/* Sul telefono le azioni scendono in fondo, dove arriva il pollice.
          Due al massimo (os-planning/mobile.md §4): sincronizzare e aprire su
          Stripe. Sospendi/Riattiva/Cancella restano nel menu in alto: sono
          irreversibili, e non si mettono sotto il pollice. */}
      <div className="m-actionbar m-only">
        <button className="btn btn-primary" onClick={() => sync.mutate()} disabled={sync.isPending}>
          <Icon name="refresh" size={12} /> {sync.isPending ? 'Sync…' : 'Sincronizza'}
        </button>
        {s.externalUrl && (
          <a className="btn" href={s.externalUrl} target="_blank" rel="noreferrer">
            <Icon name="link" size={12} /> {providerLabel(s.provider)}
          </a>
        )}
      </div>
    </div>
  );
}

